/**
 * Performance utilities for AgRentIndex
 * PR#14 — Image optimization, lazy loading, prefetch hints
 */

import React, { useState, useRef, useEffect, memo } from "react";

// ─── Lazy Image with LQIP + IntersectionObserver ────────────────────────

/**
 * LazyImage — loads images only when they enter the viewport.
 * Shows a blurred LQIP placeholder (from Sanity metadata) while loading.
 *
 * Props:
 *   src       — full image URL
 *   alt       — alt text
 *   lqip      — base64 LQIP string from Sanity (optional)
 *   width     — intrinsic width (for aspect ratio)
 *   height    — intrinsic height (for aspect ratio)
 *   className — additional classes
 *   sizes     — responsive sizes attribute
 */
export const LazyImage = memo(function LazyImage({
  src,
  alt = "",
  lqip,
  width,
  height,
  className = "",
  sizes = "100vw",
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!imgRef.current) return;
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // Start loading 200px before viewport
    );

    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, []);

  const aspectRatio = width && height ? width / height : undefined;

  return (
    <div
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      {/* LQIP placeholder */}
      {lqip && !loaded && (
        <img
          src={lqip}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover blur-lg scale-110"
        />
      )}

      {/* Actual image — only load when in viewport */}
      {inView && (
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          sizes={sizes}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
          {...rest}
        />
      )}

      {/* Fallback skeleton while not loaded */}
      {!loaded && !lqip && (
        <div className="absolute inset-0 bg-gray-100 animate-pulse" />
      )}
    </div>
  );
});

// ─── Prefetch Route ─────────────────────────────────────────────────────

/**
 * Prefetch a route's JavaScript chunk on hover/focus.
 * Wraps a Link with onMouseEnter/onFocus prefetching.
 *
 * Usage:
 *   <PrefetchLink to="/blog" loader={() => import("../pages/blog")}>
 *     Blog
 *   </PrefetchLink>
 */
export function prefetchRoute(loader) {
  let prefetched = false;
  return () => {
    if (!prefetched) {
      prefetched = true;
      loader().catch(() => {}); // Swallow errors — it's just a hint
    }
  };
}

// Prebuilt prefetchers for heavy routes
export const prefetchers = {
  blog: prefetchRoute(() => import("../pages/blog")),
  grants: prefetchRoute(() => import("../pages/grants")),
  calculator: prefetchRoute(() => import("../pages/farmland-rent-calculator")),
  location: prefetchRoute(() => import("../pages/location")),
};

// ─── Resource Hints ─────────────────────────────────────────────────────

/**
 * Add <link rel="preconnect"> for critical origins.
 * Call once on app mount.
 */
export function addResourceHints() {
  const origins = [
    "https://cdn.sanity.io",        // Sanity CDN (images, assets)
    "https://r7xr7cla.api.sanity.io", // Sanity API
    "https://fonts.googleapis.com",  // Google Fonts
    "https://fonts.gstatic.com",     // Google Fonts static
  ];

  origins.forEach((origin) => {
    if (!document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
      const link = document.createElement("link");
      link.rel = "preconnect";
      link.href = origin;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    }
  });
}

// ─── Debounce ───────────────────────────────────────────────────────────

/**
 * Simple debounce for search inputs and resize handlers.
 */
export function debounce(fn, ms = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

// ─── Format Bytes ───────────────────────────────────────────────────────

export function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
